import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";

/** List state (page, filters) kept in the URL so views are shareable and survive reloads. */
export function useListParams<K extends string>(keys: readonly K[], defaults: Partial<Record<K, string>> = {}) {
  const [params, setParams] = useSearchParams();
  const values = useMemo(() => {
    const out = {} as Record<K, string>;
    for (const k of keys) out[k] = params.get(k) ?? defaults[k] ?? "";
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keys/defaults are static per call site
  }, [params]);
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);
  const set = useCallback(
    (patch: Partial<Record<K | "page", string>>) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch) as [string, string | undefined][]) {
          if (v) next.set(k, v);
          else next.delete(k);
        }
        if (!("page" in patch)) next.delete("page");
        return next;
      }, { replace: true });
    },
    [setParams],
  );
  return { values, page, set };
}
